// AGC: SupplyDetailModal - Detailed view of a supply/input
// AGC: Shows movement history (bank statement style) and price history
// AGC: Allows quick stock entry and exit

import React, { useState, useEffect, useCallback } from 'react'
import { 
  X, 
  Plus, 
  Minus,
  ArrowLeftRight,
  DollarSign,
  Package,
  AlertTriangle,
  Check,
  TrendingUp,
  Loader2,
  Trash2,
  RotateCcw,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from '../../hooks/useTranslation'
import { useInventory } from '../../hooks/useInventory'
import { SupplyMovementHistory } from './SupplyMovementHistory'
import { SupplyPriceHistory } from './SupplyPriceHistory'
import type { StockStatus, InputCategory, InventoryItem } from '../../types/inventory'
import { getInputCategoryInfo } from '../../types/inventory'

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface SupplyDetailModalProps {
  isOpen: boolean
  onClose: () => void
  
  // AGC: Supply identification
  inputId: string
  inputName: string
  category: InputCategory
  unit: string
  
  // AGC: Current stock (from InventoryItem if exists)
  currentStock: number
  inventoryItemId?: string  // AGC: null if not in inventory yet
  
  // AGC: Lot context (optional - when opened from LotBOMCard)
  lotContext?: {
    lotId: string
    lotName: string
    requiredQuantity: number
    needToBuy: number
    stockStatus: StockStatus
  }
  
  // AGC: Threshold info
  minimumStock?: number

  // AGC: Price info
  costPerUnit?: number
  
  // AGC: Callbacks
  onStockUpdated?: () => void
  onDeleted?: () => void
}

type TabType = 'movements' | 'prices'

// =============================================================================
// AGC: STATUS BADGE
// =============================================================================

const StatusBadge: React.FC<{ status: StockStatus }> = ({ status }) => {
  const { t } = useTranslation()
  
  const config: Record<StockStatus, { bg: string; text: string; label: string }> = {
    out: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-700 dark:text-red-400',
      label: t('inventory.stockStatus.out') || 'Esgotado',
    },
    critical: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-600 dark:text-red-400',
      label: t('inventory.stockStatus.critical') || 'Crítico',
    },
    low: {
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      text: 'text-amber-700 dark:text-amber-400',
      label: t('inventory.stockStatus.low') || 'Baixo',
    },
    ok: {
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
      text: 'text-emerald-700 dark:text-emerald-400',
      label: t('inventory.stockStatus.ok') || 'OK',
    },
    overstock: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-700 dark:text-blue-400',
      label: t('inventory.stockStatus.overstock') || 'Excesso',
    },
  }
  
  const { bg, text, label } = config[status]
  
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${bg} ${text}`}>
      {label}
    </span>
  )
}

// =============================================================================
// AGC: QUICK STOCK ENTRY MODAL
// =============================================================================

interface QuickStockModalProps {
  isOpen: boolean
  onClose: () => void
  type: 'entry' | 'exit'
  inputName: string
  unit: string
  currentStock: number
  inventoryItemId?: string
  lotContext?: {
    lotId: string
    lotName: string
  }
  onConfirm: (quantity: number, reason: string, pricePerUnit?: number) => Promise<void>
}

const QuickStockModal: React.FC<QuickStockModalProps> = ({
  isOpen,
  onClose,
  type,
  inputName,
  unit,
  currentStock,
  inventoryItemId,
  lotContext,
  onConfirm,
}) => {
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState<string>('')
  const [reason, setReason] = useState('')
  const [pricePerUnit, setPricePerUnit] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // AGC: Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setQuantity('')
      setReason(type === 'entry' 
        ? (t('supplies.defaultEntryReason') || 'Compra') 
        : lotContext 
          ? `${t('supplies.applicationIn') || 'Aplicação em'} ${lotContext.lotName}`
          : ''
      )
      setPricePerUnit('')
    }
  }, [isOpen, type, lotContext, t])
  
  const handleSubmit = async () => {
    const qty = parseFloat(quantity)
    if (!qty || qty <= 0) return
    
    setIsSubmitting(true)
    try {
      const price = pricePerUnit ? parseFloat(pricePerUnit) : undefined
      await onConfirm(qty, reason, price)
      onClose()
    } catch (error) {
      console.error('AGC: Error recording stock movement:', error)
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const newStock = type === 'entry' 
    ? currentStock + (parseFloat(quantity) || 0)
    : Math.max(0, currentStock - (parseFloat(quantity) || 0))
  
  if (!isOpen) return null
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[90] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* AGC: Header */}
          <div className={`px-6 py-4 ${type === 'entry' ? 'bg-emerald-500' : 'bg-red-500'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {type === 'entry' ? (
                  <Plus className="w-6 h-6 text-white" />
                ) : (
                  <Minus className="w-6 h-6 text-white" />
                )}
                <h3 className="text-lg font-semibold text-white">
                  {type === 'entry' 
                    ? (t('supplies.addStock') || 'Agregar Stock')
                    : (t('supplies.recordExit') || 'Registrar Salida')
                  }
                </h3>
              </div>
              <button 
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <p className="text-white/80 text-sm mt-1">{inputName}</p>
          </div>
          
          {/* AGC: Content */}
          <div className="p-6 space-y-4">
            {/* AGC: Current stock info */}
            <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('inventory.currentStock') || 'Stock atual'}
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {currentStock.toFixed(1)} {unit}
              </p>
            </div>
            
            {/* AGC: Quantity input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('inventory.quantity') || 'Cantidad'} *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.1"
                  autoFocus
                  className="w-full px-4 py-3 text-lg font-semibold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {unit}
                </span>
              </div>
            </div>
            
            {/* AGC: Reason input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('inventory.reason') || 'Motivo'}
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={type === 'entry' ? 'Compra, Devolución...' : 'Aplicación, Uso...'}
                className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* AGC: Price per unit (only for entry) */}
            {type === 'entry' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('prices.pricePerUnit') || 'Precio por unidad'} ({t('prices.optional') || 'opcional'})
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                  <input
                    type="number"
                    value={pricePerUnit}
                    onChange={(e) => setPricePerUnit(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-full pl-12 pr-16 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                    /{unit}
                  </span>
                </div>
              </div>
            )}
            
            {/* AGC: New stock preview */}
            {quantity && parseFloat(quantity) > 0 && (
              <div className={`p-3 rounded-xl ${type === 'entry' ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('inventory.newStock') || 'Nuevo stock'}:
                </p>
                <p className={`text-2xl font-bold ${type === 'entry' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {newStock.toFixed(1)} {unit}
                </p>
              </div>
            )}
          </div>
          
          {/* AGC: Footer */}
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('common.cancel') || 'Cancelar'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!quantity || parseFloat(quantity) <= 0 || isSubmitting}
              className={`flex-1 py-3 px-4 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                type === 'entry' 
                  ? 'bg-emerald-500 hover:bg-emerald-600' 
                  : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {type === 'entry' ? <Plus className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
                  {t('common.confirm') || 'Confirmar'}
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// =============================================================================
// AGC: MAIN COMPONENT
// =============================================================================

export const SupplyDetailModal: React.FC<SupplyDetailModalProps> = ({
  isOpen,
  onClose,
  inputId,
  inputName,
  category,
  unit,
  currentStock,
  inventoryItemId,
  lotContext,
  minimumStock,
  costPerUnit,
  onStockUpdated,
  onDeleted,
}) => {
  const { t } = useTranslation()
  const { recordMovement, addItem, deleteItem, refresh } = useInventory()
  
  const [activeTab, setActiveTab] = useState<TabType>('movements')
  const [quickStockType, setQuickStockType] = useState<'entry' | 'exit' | null>(null)
  const [localStock, setLocalStock] = useState(currentStock)
  const [localInventoryItemId, setLocalInventoryItemId] = useState(inventoryItemId)
  // AGC: Confirmation states for destructive actions
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showZeroConfirm, setShowZeroConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isZeroing, setIsZeroing] = useState(false)
  
  // AGC: Update local stock when prop changes
  useEffect(() => {
    setLocalStock(currentStock)
    setLocalInventoryItemId(inventoryItemId)
  }, [currentStock, inventoryItemId])
  
  // AGC: Get category info
  const catInfo = getInputCategoryInfo(category)
  const categoryIcon = catInfo?.icon || '📦'
  const categoryName = catInfo?.namePt || category
  
  // AGC: Calculate stock status
  const stockStatus: StockStatus = lotContext?.stockStatus || (() => {
    if (localStock <= 0) return 'out'
    return 'ok'
  })()
  
  // AGC: Handle stock entry/exit
  const handleStockChange = useCallback(async (
    quantity: number, 
    reason: string, 
    pricePerUnit?: number,
    type: 'entry' | 'exit' = 'entry'
  ) => {
    let itemId = localInventoryItemId
    
    // AGC: If no inventory item exists, create one first
    if (!itemId && addItem) {
      console.log(`🆕 AGC: Creating inventory item for "${inputName}"`)
      try {
        const newItem = await addItem({
          name: inputName,
          inputId: inputId,
          category: category,
          quantity: 0,
          unit: unit,
          minimumStock: 0,
        })
        itemId = newItem.id
        setLocalInventoryItemId(newItem.id)
        console.log(`✅ AGC: Created inventory item with id ${newItem.id}`)
      } catch (error) {
        console.error('❌ AGC: Failed to create inventory item:', error)
        throw error
      }
    }
    
    if (!itemId || !recordMovement) {
      throw new Error('No inventory item ID or recordMovement function')
    }
    
    // AGC: Record the movement
    await recordMovement(itemId, {
      type: type,
      quantity: quantity,
      reason: reason,
      source: 'manual',
      relatedLotId: lotContext?.lotId,
    })
    
    // AGC: Update local stock
    const newStock = type === 'entry' 
      ? localStock + quantity 
      : Math.max(0, localStock - quantity)
    setLocalStock(newStock)
    
    // AGC: Refresh inventory data
    if (refresh) {
      await refresh()
    }
    
    // AGC: Notify parent
    if (onStockUpdated) {
      onStockUpdated()
    }
  }, [localInventoryItemId, addItem, recordMovement, refresh, inputName, inputId, category, unit, localStock, lotContext, onStockUpdated])

  // AGC: Handle full item deletion from database
  const handleDeleteItem = useCallback(async () => {
    if (!localInventoryItemId || !deleteItem) return
    setIsDeleting(true)
    try {
      await deleteItem(localInventoryItemId)
      if (refresh) await refresh()
      if (onDeleted) onDeleted()
      onClose()
    } catch (error) {
      console.error('❌ AGC: Error deleting inventory item:', error)
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }, [localInventoryItemId, deleteItem, refresh, onDeleted, onClose])

  // AGC: Handle zeroing out stock via an exit movement
  const handleZeroStock = useCallback(async () => {
    if (localStock <= 0) {
      setShowZeroConfirm(false)
      return
    }
    let itemId = localInventoryItemId
    if (!itemId && addItem) {
      try {
        const newItem = await addItem({ name: inputName, inputId, category, quantity: 0, unit, minimumStock: 0 })
        itemId = newItem.id
        setLocalInventoryItemId(newItem.id)
      } catch (error) {
        console.error('❌ AGC: Failed to create inventory item for zeroing:', error)
        setShowZeroConfirm(false)
        return
      }
    }
    if (!itemId || !recordMovement) {
      setShowZeroConfirm(false)
      return
    }
    setIsZeroing(true)
    try {
      await recordMovement(itemId, {
        type: 'exit',
        quantity: localStock,
        reason: t('supplies.zeroStockReason') || 'Zerar estoque',
        source: 'manual',
        relatedLotId: lotContext?.lotId,
      })
      setLocalStock(0)
      if (refresh) await refresh()
      if (onStockUpdated) onStockUpdated()
    } catch (error) {
      console.error('❌ AGC: Error zeroing stock:', error)
    } finally {
      setIsZeroing(false)
      setShowZeroConfirm(false)
    }
  }, [localStock, localInventoryItemId, addItem, recordMovement, refresh, inputName, inputId, category, unit, lotContext, onStockUpdated, t])

  if (!isOpen) return null
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white dark:bg-slate-800 w-full sm:w-[550px] sm:max-h-[85vh] max-h-[90vh] rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* AGC: Header */}
          <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {/* AGC: Category icon */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  stockStatus === 'out' || stockStatus === 'critical' 
                    ? 'bg-red-100 dark:bg-red-900/30' 
                    : stockStatus === 'low'
                    ? 'bg-amber-100 dark:bg-amber-900/30'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <span className="text-3xl">{categoryIcon}</span>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {inputName}
                    </h2>
                    <StatusBadge status={stockStatus} />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
                    {categoryName} • {unit}
                  </p>
                </div>
              </div>
              
              {/* AGC: Top-right actions: delete item + close */}
              <div className="flex items-center gap-1">
                {localInventoryItemId && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    title={t('inventory.deleteItem') || 'Excluir item'}
                    className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-colors group"
                  >
                    <Trash2 className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors" />
                  </button>
                )}
                <button 
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            {/* AGC: Stock info */}
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('supplies.currentStock') || 'Stock Actual'}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {localStock.toFixed(1)} <span className="text-lg font-normal text-gray-400">{unit}</span>
                  </p>
                </div>
                
                {minimumStock != null && minimumStock > 0 && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <AlertTriangle className={`w-3.5 h-3.5 ${localStock < minimumStock ? 'text-red-500' : 'text-gray-400'}`} />
                    <p className={`text-xs ${localStock < minimumStock ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                      {t('inventory.minimumStock') || 'Estoque mínimo'}: {minimumStock} {unit}
                    </p>
                  </div>
                )}

                {lotContext && (
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('supplies.required') || 'Necesario'}
                    </p>
                    <p className="text-xl font-medium text-gray-700 dark:text-gray-300">
                      {lotContext.requiredQuantity.toFixed(1)} {unit}
                    </p>
                    <p className="text-xs text-gray-400">
                      {t('supplies.forLot') || 'Para'}: {lotContext.lotName}
                    </p>
                  </div>
                )}
                
                {!lotContext && costPerUnit && costPerUnit > 0 && (
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('supplies.unitPrice') || 'Precio/Un'}
                    </p>
                    <p className="text-xl font-medium text-gray-700 dark:text-gray-300">
                      R$ {costPerUnit.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {/* AGC: Quick action buttons */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setQuickStockType('entry')}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors"
              >
                <Plus className="w-5 h-5" />
                {t('supplies.addStock') || 'Adicionar Estoque'}
              </button>
              <button
                onClick={() => setQuickStockType('exit')}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors"
              >
                <Minus className="w-5 h-5" />
                {t('supplies.recordExit') || 'Registrar Saída'}
              </button>
              {/* AGC: Zero stock button - only shown when stock > 0 */}
              {localStock > 0 && (
                <button
                  onClick={() => setShowZeroConfirm(true)}
                  title={t('supplies.zeroStock') || 'Zerar Estoque'}
                  className="flex items-center justify-center p-3 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-xl font-medium transition-colors border border-amber-200 dark:border-amber-700"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
          
          {/* AGC: Tabs */}
          <div className="border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <div className="flex">
              <button
                onClick={() => setActiveTab('movements')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'movements'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <ArrowLeftRight className="w-4 h-4" />
                {t('supplies.movementsTab') || 'Movimientos'}
              </button>
              <button
                onClick={() => setActiveTab('prices')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'prices'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                {t('supplies.pricesTab') || 'Precios'}
              </button>
            </div>
          </div>
          
          {/* AGC: Tab content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'movements' ? (
              <SupplyMovementHistory 
                inventoryItemId={localInventoryItemId}
                inputName={inputName}
                unit={unit}
              />
            ) : (
              <SupplyPriceHistory 
                inventoryItemId={localInventoryItemId}
                inputName={inputName}
                unit={unit}
              />
            )}
          </div>
        </motion.div>
        
        {/* AGC: Quick stock entry/exit modal */}
        <QuickStockModal
          isOpen={quickStockType !== null}
          onClose={() => setQuickStockType(null)}
          type={quickStockType || 'entry'}
          inputName={inputName}
          unit={unit}
          currentStock={localStock}
          inventoryItemId={localInventoryItemId}
          lotContext={lotContext}
          onConfirm={async (quantity, reason, pricePerUnit) => {
            await handleStockChange(quantity, reason, pricePerUnit, quickStockType || 'entry')
          }}
        />

        {/* AGC: Delete item confirmation dialog */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
              onClick={() => setShowDeleteConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {t('inventory.deleteItem') || 'Excluir Item'}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                    {t('supplies.deleteConfirmMessage') || `Tem certeza que deseja excluir "${inputName}" do banco de dados? Esta ação não pode ser desfeita.`}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                      {t('common.cancel') || 'Cancelar'}
                    </button>
                    <button
                      onClick={handleDeleteItem}
                      disabled={isDeleting}
                      className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          {t('common.delete') || 'Excluir'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AGC: Zero stock confirmation dialog */}
        <AnimatePresence>
          {showZeroConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
              onClick={() => setShowZeroConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <RotateCcw className="w-8 h-8 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {t('supplies.zeroStock') || 'Zerar Estoque'}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">
                    {t('supplies.zeroStockConfirmMessage') || 'Isso irá registrar uma saída de todo o estoque atual:'}
                  </p>
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mb-6">
                    {localStock.toFixed(1)} {unit} → 0
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowZeroConfirm(false)}
                      disabled={isZeroing}
                      className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                      {t('common.cancel') || 'Cancelar'}
                    </button>
                    <button
                      onClick={handleZeroStock}
                      disabled={isZeroing}
                      className="flex-1 py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    >
                      {isZeroing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4" />
                          {t('supplies.zeroStock') || 'Zerar'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}

export default SupplyDetailModal
